/**
 * <SmartTextInput /> — drop-in replacement for `<textarea>` that adds:
 *   1. An AI toolbar: ✨ Improve · Fix grammar · Expand · Shorten
 *      Each button calls `POST /api/ai/rewrite` and shows a variant picker.
 *   2. A language input dropdown that hooks the free Google Input Tools API
 *      to give real-time transliteration suggestions (e.g. type "namaste" in
 *      Latin → get "नमस्ते" as a tap-to-insert pill). No backend key needed.
 *
 * Props:
 *   value            controlled string
 *   onChange(next)   controlled setter
 *   placeholder      passthrough
 *   rows             passthrough (default 4)
 *   maxLength        passthrough
 *   className        merged onto the <textarea>
 *   style            merged onto the <textarea>
 *   testid           base data-testid; buttons get suffixes
 *   ai               true (default) — show AI toolbar
 *   translit         true (default) — show language dropdown
 *
 * Uses only public APIs. Falls back gracefully when either service is down.
 */
import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { Sparkles, WandSparkles, Feather, Minimize2, Languages, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

const AI_MODES = [
  { key: "improve",     label: "Improve",     Icon: Sparkles },
  { key: "fix_grammar", label: "Fix grammar", Icon: WandSparkles },
  { key: "expand",      label: "Expand",      Icon: Feather },
  { key: "shorten",     label: "Shorten",     Icon: Minimize2 },
];

// Google Input Tools language pack. `itc` is the transliteration code.
// See https://www.google.com/inputtools/help/apis.html
const LANGS = [
  { code: "off", label: "English (no transliteration)" },
  { code: "hi",  label: "हिन्दी · Hindi" },
  { code: "bn",  label: "বাংলা · Bengali" },
  { code: "ta",  label: "தமிழ் · Tamil" },
  { code: "te",  label: "తెలుగు · Telugu" },
  { code: "mr",  label: "मराठी · Marathi" },
  { code: "gu",  label: "ગુજરાતી · Gujarati" },
  { code: "kn",  label: "ಕನ್ನಡ · Kannada" },
  { code: "ml",  label: "മലയാളം · Malayalam" },
  { code: "pa",  label: "ਪੰਜਾਬੀ · Punjabi" },
  { code: "ur",  label: "اردو · Urdu" },
  { code: "ar",  label: "العربية · Arabic" },
  { code: "fa",  label: "فارسی · Persian" },
  { code: "ru",  label: "Русский · Russian" },
  { code: "sr",  label: "Српски · Serbian" },
  { code: "el",  label: "Ελληνικά · Greek" },
  { code: "he",  label: "עברית · Hebrew" },
  { code: "am",  label: "አማርኛ · Amharic" },
  { code: "th",  label: "ไทย · Thai" },
  { code: "ne",  label: "नेपाली · Nepali" },
];

const TRANSLIT_ENDPOINT = "https://inputtools.google.com/request";

async function fetchTransliteration(word, langCode) {
  if (!word || langCode === "off") return [];
  const url = `${TRANSLIT_ENDPOINT}?text=${encodeURIComponent(word)}&itc=${langCode}-t-i0-und&num=5&cp=0&cs=1&ie=utf-8&oe=utf-8&app=demopage`;
  try {
    const res = await fetch(url, { method: "GET" });
    if (!res.ok) return [];
    const data = await res.json();
    // Response shape: ["SUCCESS", [["namaste", ["नमस्ते","नमशते",…], [], {}]]]
    if (Array.isArray(data) && data[0] === "SUCCESS" && data[1]?.[0]?.[1]) {
      return data[1][0][1];
    }
    return [];
  } catch { return []; }
}

export default function SmartTextInput({
  value = "",
  onChange,
  placeholder,
  rows = 4,
  maxLength = 5000,
  className = "",
  style = {},
  testid = "smart-text-input",
  ai = true,
  translit = true,
}) {
  const [busyMode, setBusyMode] = useState(null);
  const [variants, setVariants] = useState(null); // {mode, list}
  const [lang, setLang] = useState("off");
  const [suggestions, setSuggestions] = useState([]);
  const [suggWord, setSuggWord] = useState("");
  const [suggPos, setSuggPos] = useState({ start: 0, end: 0 });
  const textareaRef = useRef(null);
  const debounceRef = useRef(null);

  // --- AI rewrite -----------------------------------------------------
  const runRewrite = async (mode) => {
    const text = (value || "").trim();
    if (!text) { toast.error("Type something first."); return; }
    setBusyMode(mode);
    setVariants(null);
    try {
      const { data } = await http.post("/ai/rewrite", { text, mode, variants: 2 });
      if (!data?.ok) {
        toast.error("AI is busy right now. Try again in a bit.");
        return;
      }
      setVariants({ mode, list: data.variants || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reach the AI.");
    } finally { setBusyMode(null); }
  };

  const acceptVariant = (v) => {
    onChange?.(v);
    setVariants(null);
  };

  // --- Transliteration ------------------------------------------------
  const handleChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
    if (!translit || lang === "off") { setSuggestions([]); return; }
    // Figure out the "current word" — from the last whitespace/newline to the caret.
    const el = e.target;
    const pos = el.selectionStart ?? v.length;
    const before = v.slice(0, pos);
    const match = before.match(/(\S+)$/);
    if (!match) { setSuggestions([]); return; }
    const word = match[1];
    const start = pos - word.length;
    setSuggWord(word);
    setSuggPos({ start, end: pos });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const s = await fetchTransliteration(word, lang);
      setSuggestions(s);
    }, 220);
  };

  const applySuggestion = (s) => {
    const before = value.slice(0, suggPos.start);
    const after = value.slice(suggPos.end);
    const next = `${before}${s} ${after}`;
    onChange?.(next);
    setSuggestions([]);
    // Refocus and place caret right after the inserted word.
    setTimeout(() => {
      const el = textareaRef.current;
      if (el) {
        const newPos = before.length + s.length + 1;
        el.focus();
        el.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  return (
    <div className="w-full" data-testid={testid}>
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value || ""}
          onChange={handleChange}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          className={`wofy-input w-full ${className}`}
          style={style}
          data-testid={`${testid}-textarea`}
        />
        {translit && lang !== "off" && suggestions.length > 0 && (
          <div
            className="mt-1 flex flex-wrap gap-1 items-center px-2 py-1.5 rounded-lg border text-sm"
            style={{ background: "var(--wofy-surface)", borderColor: "var(--wofy-line)" }}
            data-testid={`${testid}-translit-suggestions`}
          >
            <span className="text-[10px] font-mono opacity-60 mr-1">{suggWord} →</span>
            {suggestions.map((s, i) => (
              <button
                key={`${s}-${i}`}
                type="button"
                onClick={() => applySuggestion(s)}
                className="px-2 py-0.5 rounded-full border text-sm hover:bg-black/[0.04]"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid={`${testid}-translit-pick-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {(ai || translit) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {ai && AI_MODES.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => runRewrite(key)}
              disabled={busyMode !== null}
              className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-full border hover:bg-black/[0.04] transition"
              style={{ borderColor: "var(--wofy-line)", color: "var(--wofy-text)" }}
              data-testid={`${testid}-ai-${key}`}
              title={`AI: ${label}`}
            >
              {busyMode === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
              {label}
            </button>
          ))}
          {translit && (
            <label className="text-xs inline-flex items-center gap-1 ml-auto" data-testid={`${testid}-lang-wrap`}>
              <Languages className="w-3 h-3 opacity-70" />
              <select
                value={lang}
                onChange={(e) => { setLang(e.target.value); setSuggestions([]); }}
                className="text-xs bg-transparent border rounded-full px-2 py-1"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid={`${testid}-lang-select`}
              >
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </label>
          )}
        </div>
      )}

      {variants && variants.list.length > 0 && (
        <div className="mt-3 rounded-xl border p-3" style={{ borderColor: "var(--wofy-primary)", background: "rgba(178,93,56,0.03)" }} data-testid={`${testid}-variants`}>
          <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
            <Sparkles className="w-3 h-3 inline -mt-0.5 mr-1" /> AI SUGGESTIONS · {variants.mode.replace("_", " ")}
          </div>
          <div className="space-y-2">
            {variants.list.map((v, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="flex-1 text-sm rounded-lg p-2 bg-white border" style={{ borderColor: "var(--wofy-line)" }}>{v}</div>
                <button
                  type="button"
                  onClick={() => acceptVariant(v)}
                  className="wofy-btn-primary text-xs !px-2 !py-1"
                  aria-label="Use this rewrite"
                  data-testid={`${testid}-variant-use-${i}`}
                >
                  <Check className="w-3 h-3" /> Use
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setVariants(null)}
            className="text-[10px] mt-2 underline opacity-70"
            data-testid={`${testid}-variants-dismiss`}
          >
            <X className="w-3 h-3 inline -mt-0.5 mr-0.5" /> Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
